import { useState } from 'react';

import type Enums from '@core/types/api/Enums';

import {
  type AllowedArchived,
  type ArchiveKey,
  type UseArchiveReturn,
  useArchiveManager,
} from './useArchiveManager/useArchiveManager';

interface UseDrawerArchiveProps<T extends ArchiveKey> {
  entity: T;
  /** When set, only users with one of these roles may archive. Omit for unrestricted access. */
  allowedRoles?: Enums.RoleName[];
  /** Read the archived flag from the archive mutation result. */
  getIsArchived: (data: AllowedArchived[T]['returns']) => boolean;
  /** Called after a successful archive/restore (e.g. to close/refresh the drawer). */
  onSuccess?: (data: AllowedArchived[T]['returns']) => void;
}

interface UseDrawerArchiveReturn<T extends ArchiveKey> {
  confirmAndArchive: UseArchiveReturn<T>['confirmAndArchive'];
  isArchiveLoading: boolean;
  canArchive: boolean;
  /**
   * Resolve the archived state for the footer. The mutation result wins over the
   * query-derived `base` until the invalidated detail query refetches, so the
   * footer flips immediately instead of briefly showing the pre-archive actions.
   */
  resolveIsArchived: (base: boolean) => boolean;
}

/**
 * Archive helper for entity drawers that drive `DrawerFormFooter` from a
 * query-derived `isArchived`. Tracks the latest archived flag from the mutation
 * result so the footer cannot lag the refetch and let a second click send the
 * opposite toggle.
 */
export const useDrawerArchive = <T extends ArchiveKey>({
  entity,
  allowedRoles,
  getIsArchived,
  onSuccess,
}: UseDrawerArchiveProps<T>): UseDrawerArchiveReturn<T> => {
  const [override, setOverride] = useState<boolean | null>(null);

  const { confirmAndArchive, isLoading, canArchive } = useArchiveManager({
    entity,
    allowedRoles,
    onSuccess: (data) => {
      setOverride(getIsArchived(data));
      onSuccess?.(data);
    },
  });

  return {
    confirmAndArchive,
    isArchiveLoading: isLoading,
    canArchive,
    resolveIsArchived: (base): boolean => override ?? base,
  };
};
